import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WrapTextIcon from '@mui/icons-material/WrapText';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { PreviewCategory } from '../../utils/previewUtils';

export interface PreviewToolbarNav {
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  canPrev: boolean;
  canNext: boolean;
}

export interface PreviewToolbarProps {
  variant?: 'full' | 'compact';
  category: PreviewCategory;
  fileName?: string;
  imageZoom?: number;
  imageRotation?: number;
  pdfScale?: number;
  isFullscreen?: boolean;
  textWordWrap?: boolean;
  textFontSize?: number;
  nav?: PreviewToolbarNav;
  showDownload?: boolean;
  showPrint?: boolean;
  showRetry?: boolean;
  onImageZoomChange?: (zoom: number) => void;
  onImageRotationChange?: (rotation: number) => void;
  onImageReset?: () => void;
  onPdfScaleChange?: (scale: number) => void;
  onPdfFit?: () => void;
  onFullscreenToggle?: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
  onOpenNewTab?: () => void;
  onCopyText?: () => void;
  onTextWordWrapToggle?: () => void;
  onTextFontSizeChange?: (size: number) => void;
  onRetry?: () => void;
}

export const PreviewToolbar: React.FC<PreviewToolbarProps> = ({
  variant = 'full',
  category,
  fileName,
  imageZoom = 1,
  imageRotation = 0,
  pdfScale = 1,
  isFullscreen = false,
  textWordWrap = true,
  textFontSize = 14,
  nav,
  showDownload = true,
  showPrint = true,
  showRetry = false,
  onImageZoomChange,
  onImageRotationChange,
  onImageReset,
  onPdfScaleChange,
  onPdfFit,
  onFullscreenToggle,
  onDownload,
  onPrint,
  onOpenNewTab,
  onCopyText,
  onTextWordWrapToggle,
  onTextFontSizeChange,
  onRetry,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const dense = variant === 'compact' || isMobile;

  const renderImageControls = () => (
    <>
      <Tooltip title="Zoom out">
        <span>
          <IconButton
            size="small"
            disabled={imageZoom <= 0.5}
            onClick={() => onImageZoomChange?.(Math.max(0.5, imageZoom - 0.2))}
            aria-label="Zoom out"
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'center' }}>
        {Math.round(imageZoom * 100)}%
      </Typography>
      <Tooltip title="Zoom in">
        <span>
          <IconButton
            size="small"
            disabled={imageZoom >= 3}
            onClick={() => onImageZoomChange?.(Math.min(3, imageZoom + 0.2))}
            aria-label="Zoom in"
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Rotate">
        <IconButton
          size="small"
          onClick={() => onImageRotationChange?.((imageRotation + 90) % 360)}
          aria-label="Rotate"
        >
          <Rotate90DegreesCcwIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Reset view">
        <IconButton size="small" onClick={onImageReset} aria-label="Reset view">
          <RestartAltIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  const renderPdfControls = () => (
    <>
      <Tooltip title="Zoom out">
        <span>
          <IconButton
            size="small"
            disabled={pdfScale <= 0.5}
            onClick={() => onPdfScaleChange?.(Math.max(0.5, pdfScale - 0.1))}
            aria-label="Zoom out PDF"
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'center' }}>
        {Math.round(pdfScale * 100)}%
      </Typography>
      <Tooltip title="Zoom in">
        <span>
          <IconButton
            size="small"
            disabled={pdfScale >= 2.5}
            onClick={() => onPdfScaleChange?.(Math.min(2.5, pdfScale + 0.1))}
            aria-label="Zoom in PDF"
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Fit width">
        <IconButton size="small" onClick={onPdfFit} aria-label="Fit width">
          <RestartAltIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {onOpenNewTab && (
        <Tooltip title="Open in new tab">
          <IconButton size="small" onClick={onOpenNewTab} aria-label="Open in new tab">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  const renderTextControls = () => (
    <>
      <Tooltip title="Copy">
        <IconButton size="small" onClick={onCopyText} aria-label="Copy text">
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={textWordWrap ? 'Disable wrap' : 'Enable wrap'}>
        <IconButton size="small" onClick={onTextWordWrapToggle} aria-label="Toggle word wrap">
          <WrapTextIcon fontSize="small" color={textWordWrap ? 'primary' : 'inherit'} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Smaller font">
        <IconButton
          size="small"
          onClick={() => onTextFontSizeChange?.(Math.max(10, textFontSize - 1))}
          aria-label="Decrease font size"
        >
          <TextDecreaseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Larger font">
        <IconButton
          size="small"
          onClick={() => onTextFontSizeChange?.(Math.min(20, textFontSize + 1))}
          aria-label="Increase font size"
        >
          <TextIncreaseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  const commonActions = (
    <>
      {showRetry && onRetry && (
        <Tooltip title="Retry">
          <IconButton size="small" onClick={onRetry} aria-label="Retry preview">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {showDownload && onDownload && (
        <Tooltip title="Download">
          <IconButton size="small" onClick={onDownload} aria-label="Download">
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {showPrint && onPrint && (category === 'image' || category === 'pdf' || category === 'text') && (
        <Tooltip title="Print">
          <IconButton size="small" onClick={onPrint} aria-label="Print">
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onFullscreenToggle && ['image', 'pdf', 'video'].includes(category) && (
        <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
          <IconButton size="small" onClick={onFullscreenToggle} aria-label="Toggle fullscreen">
            {isFullscreen ? (
              <FullscreenExitIcon fontSize="small" />
            ) : (
              <FullscreenIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  const categoryControls =
    category === 'image'
      ? renderImageControls()
      : category === 'pdf'
        ? renderPdfControls()
        : category === 'text'
          ? renderTextControls()
          : null;

  const overflowItems: React.ReactNode[] = [];
  if (isMobile && categoryControls) {
    overflowItems.push(categoryControls);
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: dense ? 1 : 2,
        py: 0.5,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        flexWrap: 'wrap',
        minHeight: 40,
      }}
    >
      {nav && variant === 'full' && (
        <>
          <Tooltip title="Previous file">
            <span>
              <IconButton
                size="small"
                disabled={!nav.canPrev}
                onClick={nav.onPrev}
                aria-label="Previous file"
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, textAlign: 'center' }}>
            {nav.index + 1} / {nav.total}
          </Typography>
          <Tooltip title="Next file">
            <span>
              <IconButton
                size="small"
                disabled={!nav.canNext}
                onClick={nav.onNext}
                aria-label="Next file"
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        </>
      )}

      {!isMobile && categoryControls}
      {!isMobile && categoryControls && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}
      {!isMobile && commonActions}

      {isMobile && (
        <>
          {commonActions}
          {(categoryControls || showRetry) && (
            <>
              <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="More actions">
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                {category === 'image' && (
                  <>
                    <MenuItem onClick={() => { onImageZoomChange?.(Math.min(3, imageZoom + 0.2)); setMenuAnchor(null); }}>
                      <ListItemIcon><ZoomInIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>Zoom in</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { onImageZoomChange?.(Math.max(0.5, imageZoom - 0.2)); setMenuAnchor(null); }}>
                      <ListItemIcon><ZoomOutIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>Zoom out</ListItemText>
                    </MenuItem>
                  </>
                )}
                {onDownload && (
                  <MenuItem onClick={() => { onDownload(); setMenuAnchor(null); }}>
                    <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Download</ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </>
      )}

      {fileName && dense && (
        <Typography variant="caption" noWrap sx={{ flex: 1, ml: 1, textAlign: 'right' }} title={fileName}>
          {fileName}
        </Typography>
      )}
    </Box>
  );
};

export default PreviewToolbar;
